import axios, { AxiosInstance } from "axios";
import {
  clearAllSessions,
  clearGoogleDriveState,
  getAccessTokenSession,
  getRefreshTokenSession,
  setAccessTokenSession,
  setRefreshTokenSession,
} from "../utility/common/session-store";
import { ErrorEnum } from "../utility/enum/error.enum";
import { AuthBridge } from "../utility/AuthBridge";
import { useAppDispatch } from "../store/hooks";
import { updateToken } from "../features/user/userSlice";
import { store } from "../store";


export const updateHeaderInterceptor = (axiosInstance: AxiosInstance) => {
  const noAuthUrls = [
    "login",
    "loginViaId",
    "loginGoogleUser",
    "reset",
    "refresh",
    "hosts",
    "getFilesListFromGoogleDrive",
    "providers",
    "templates",
  ];
  axiosInstance.interceptors.request.use(
    async (config) => {
      const isUsers = config.url === "users";
      const shouldSkipAuth = noAuthUrls.some(
        (url) => config.url?.includes(url) || isUsers
      );

      if (!shouldSkipAuth || config.url?.includes("rentalSummary")) {
        const token = getAccessTokenSession();
        if (token) {
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      if (error === ErrorEnum.None) {
        AuthBridge.logout();
        AuthBridge.openLoginModal();
      }
      return Promise.reject(error);
    }
  );
};

const refreshAxios = axios.create();
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

export const errorInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as any;

      if (!error.response) {
        AuthBridge.logout();
        AuthBridge.openLoginModal();
        return Promise.reject(error);
      }

      const status = error.response.status;

      // handle 401
      if (
        status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes("/auth/token/refresh")
      ) {
        originalRequest._retry = true;

        try {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              const accessToken = getAccessTokenSession();
              const refreshToken = getRefreshTokenSession();

              const resp = await refreshAxios.post(
                `${process.env.REACT_APP_API_BASE_URL}auth/token/refresh`,
                {
                  token: accessToken,
                  refresh_token: refreshToken,
                }
              );

              const data = resp.data.data;
              setAccessTokenSession(data.token);
              setRefreshTokenSession(data.refresh_token);
              store.dispatch(updateToken({ access_token: data.token }));

              return data.token;
            })();

            refreshPromise.finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
          }

          const newToken = await refreshPromise;

          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          clearAllSessions();
          AuthBridge.logout();
          AuthBridge.openLoginModal();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
